import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dailyCareLogsApi, ComplianceRow, DailySummary } from '../api/dailyCareLogs';

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const DailyCareCompliance: React.FC = () => {
  const navigate = useNavigate();
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(todayStr());
  const [date, setDate] = useState(todayStr());

  const [rows, setRows] = useState<ComplianceRow[]>([]);
  const [missing, setMissing] = useState<{ id: number; name: string }[]>([]);
  const [summary, setSummary] = useState<DailySummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [compliance, miss, summ] = await Promise.all([
        dailyCareLogsApi.compliance(from, to),
        dailyCareLogsApi.missingLogs(date),
        dailyCareLogsApi.summary(date)
      ]);
      setRows(compliance);
      setMissing(miss.data);
      setSummary(summ);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [from, to, date]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Compliance & Alerts</h1>
          <p className="text-sm text-gray-600 mt-1">Who submitted what, and who missed today's log</p>
        </div>
        <button
          onClick={() => navigate('/daily-care-logs')}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          &larr; Back to logs
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Compliance from</label>
            <input
              type="date"
              value={from}
              onChange={e => setFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Compliance to</label>
            <input
              type="date"
              value={to}
              onChange={e => setTo(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Missing-logs date</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={load}
              className="w-full px-4 py-2 bg-gray-800 text-white rounded-md text-sm font-medium hover:bg-black"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4 text-sm">{error}</div>
      )}

      {/* Summary cards for selected date */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs text-gray-600 uppercase tracking-wider">Total logs ({date})</div>
            <div className="text-3xl font-bold text-gray-900 mt-1">{summary.totalLogs}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs text-gray-600 uppercase tracking-wider">Residents covered</div>
            <div className="text-3xl font-bold text-gray-900 mt-1">{summary.uniqueResidents}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-xs text-gray-600 uppercase tracking-wider">Carers active</div>
            <div className="text-3xl font-bold text-gray-900 mt-1">{summary.uniqueCarers}</div>
          </div>
          <div className={`bg-white rounded-lg shadow p-4 border-l-4 ${missing.length > 0 ? 'border-red-500' : 'border-green-500'}`}>
            <div className="text-xs text-gray-600 uppercase tracking-wider">Missing today</div>
            <div className={`text-3xl font-bold mt-1 ${missing.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {missing.length}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Carer submissions */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Carer submissions ({from} → {to})</h2>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Carer</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Logs</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Residents</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Days</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500 text-sm">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500 text-sm">No submissions in this range</td></tr>
              )}
              {!loading && rows.map(r => (
                <tr key={r.carerId} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-900 font-medium">{r.name || `#${r.carerId}`}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{r.logsSubmitted}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{r.distinctResidents}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{r.distinctDays}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Missing logs */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Residents with NO log on {date}</h2>
          </div>
          {missing.length === 0 ? (
            <p className="p-6 text-center text-green-700 text-sm">All residents have a log for this date ✓</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {missing.map(r => (
                <li key={r.id} className="px-4 py-3 text-sm flex items-center">
                  <span className="text-red-600 mr-2">⚠</span>
                  <span className="text-gray-900">{r.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Per-category breakdown */}
      {summary && summary.perCategory.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4 mt-4">
          <h2 className="font-semibold text-gray-900 mb-3">Category breakdown ({date})</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {summary.perCategory.map(c => (
              <div key={c.slug} className="bg-gray-50 rounded p-3 text-center">
                <div className="text-xs text-gray-600">{c.name}</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{c.count}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyCareCompliance;
